/**
 * Newsletter FEEDBACK link — the one contract shared by the email (render.ts / links.ts) and the portal's bug
 * modal (components/BugReportButton.tsx).
 *
 * MECHANISM (operator question, Sept 4 2026 — "will that automatically file a bug?"): the button in the email opens
 * the portal with `?feedback=newsletter-<month>`; the bug modal sees that param, opens itself PREFILLED (title, area
 * "Newsletter — <Month>", a prompt in the steps box), and the reader's submit goes through the ordinary
 * `POST /api/bug-reports` — so feedback IS a bug-report entry: project Portal, reporter = the signed-in team member,
 * visible in Settings › Bugs and mailed to the operator by the bug pipeline like any report. Nothing is created
 * automatically by the click itself; only a submitted form files anything. The area prefix keeps feedback rows
 * recognisable (and filterable) among real bugs.
 *
 * PURE + client-safe: no IO, no server imports — the modal imports this too.
 */
export const NEWSLETTER_FEEDBACK_PARAM = 'feedback'
const SLUG_RE = /^newsletter-(\d{4}-\d{2})$/

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export const feedbackMonthLabel = (month: string): string => {
  const [y, m] = month.split('-')
  return `${MONTHS[Number(m) - 1] ?? m} ${y}`
}

/** The value the email puts in `?feedback=`. */
export const newsletterFeedbackSlug = (month: string): string => `newsletter-${month}`

/** Parse a `?feedback=` value; null for anything that is not a newsletter slug (the modal ignores it). */
export function parseNewsletterFeedbackSlug(value: string | null | undefined): { month: string } | null {
  const m = SLUG_RE.exec(value ?? '')
  return m ? { month: m[1] } : null
}

/** What the modal prefills for a month's edition. The steps text is a prompt the reader overwrites. */
export function newsletterFeedbackPrefill(month: string): { title: string; area: string; steps: string; description: string } {
  const label = feedbackMonthLabel(month)
  return {
    title: `Feedback — ${label} Market Report`,
    area: `Newsletter — ${label}`,
    steps: 'What was useful, what was missing or wrong, and what should next month\'s report do differently?',
    description: `Reader feedback on the ${label} market-intelligence email.`,
  }
}
